import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { compensationKeys } from './queryKeys';
import { toast } from '@/hooks/use-toast';
import type { CompensationRule } from '@/services/compensation/types';

// Helper function to log assignment history
const logAssignmentHistory = async (
  action: 'created' | 'updated' | 'assigned' | 'unassigned' | 'deactivated' | 'bulk_assigned',
  rule: Partial<CompensationRule>,
  profileId?: string,
  reason?: string,
  oldValues?: Record<string, any>,
  newValues?: Record<string, any>
) => {
  try {
    const { data: user } = await supabase.auth.getUser();

    const historyEntry: any = {
      company_id: rule.company_id,
      compensation_rule_id: rule.id,
      action,
      rule_name: rule.rule_name || '',
      rule_type: rule.rule_type || '',
      performed_by: user?.user?.id,
      old_values: oldValues || {},
      new_values: newValues || {
        base_rate: rule.base_rate,
        percentage_rate: rule.percentage_rate,
        multiplier_rate: rule.multiplier_rate,
        threshold_hours: rule.threshold_hours,
        workweek_days: rule.workweek_days,
        scheduled_days: rule.scheduled_days,
        ot_tiers: rule.ot_tiers,
        holiday_multiplier: rule.holiday_multiplier
      },
      reason
    };

    // If profileId is available, include it in the history entry
    if (profileId) {
      historyEntry.profile_id = profileId;
    }

    await supabase.from('compensation_assignment_history').insert(historyEntry);
  } catch (error) {
    console.error('Failed to log assignment history:', error);
  }
};

export const useCompensationMutations = () => {
  const queryClient = useQueryClient();

  // Create compensation rule mutation
  const createCompensationRule = useMutation({
    mutationFn: async (rule: Omit<CompensationRule, 'id' | 'created_at' | 'updated_at'>) => {
      try {
        // Rules are now templates without specific employee assignments
        const { data, error } = await supabase
          .from('compensation_rules')
          .insert(rule)
          .select()
          .single();

        if (error) throw error;

        const newRule = data as CompensationRule;

        // Log rule creation (no specific profileId since rules are now generic)
        await logAssignmentHistory('created', newRule, undefined, 'New compensation rule created');

        return newRule;
      } catch (error) {
        console.error('[useCompensationMutations] Error creating rule:', error);
        throw error;
      }
    },
    onMutate: async (newRule) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: compensationKeys.rules() });

      // Snapshot previous value
      const previousRules = queryClient.getQueryData(compensationKeys.rules());

      // Optimistic update for immediate UI feedback
      const optimisticRule = {
        ...newRule,
        id: `temp-${Date.now()}`,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      // Optimistically update company rules list
      queryClient.setQueryData(compensationKeys.rules(), (old: CompensationRule[] = []) => [
        ...old,
        optimisticRule
      ]);

      return { previousRules };
    },
    onError: (error, variables, context) => {
      // Restore cache on error
      if (context?.previousRules) {
        queryClient.setQueryData(compensationKeys.rules(), context.previousRules);
      }

      const errorMessage = error.message || "Failed to create compensation rule.";
      console.error('[useCompensationMutations] Create rule error:', error);
      
      toast({
        title: "Error Creating Rule",
        description: errorMessage,
        variant: "destructive",
        duration: 8000,
      });
    },
    onSuccess: (newRule) => {
      toast({
        title: "Rule Created",
        description: `${newRule.rule_name} has been created successfully.`,
        duration: 5000,
      });
    },
    onSettled: () => {
      // Invalidate and refetch rules
      queryClient.invalidateQueries({ queryKey: compensationKeys.rules() });
      queryClient.invalidateQueries({ queryKey: [...compensationKeys.all, 'company-rules'] });
    }
  });

  // Update compensation rule mutation
  const updateCompensationRule = useMutation({
    mutationFn: async ({ id, ...rule }: Partial<CompensationRule> & { id: string }) => {
      try {
        // Since rules no longer have specific employee assignments, validation is handled at assignment level
        const { data, error } = await supabase
          .from('compensation_rules')
          .update(rule)
          .eq('id', id)
          .select()
          .single();

        if (error) throw error;

        const updatedRule = data as CompensationRule;

        // Log rule update (no specific profileId since rules are now generic)
        await logAssignmentHistory('updated', updatedRule, undefined, 'Compensation rule updated');

        return updatedRule;
      } catch (error) {
        console.error('[useCompensationMutations] Error updating rule:', error);
        throw error;
      }
    },
    onSuccess: (updatedRule) => {
      toast({
        title: "Rule Updated",
        description: `${updatedRule.rule_name} has been updated successfully.`,
        duration: 5000,
      });
    },
    // onSettled removed - invalidation is now manual after dialog closes
    // This prevents DOM race conditions with React portals during dialog unmounting
  });

  // Deactivate compensation rule mutation
  const deactivateCompensationRule = useMutation({
    mutationFn: async (ruleId: string) => {
      try {
        const { data, error } = await supabase
          .from('compensation_rules')
          .update({ is_active: false })
          .eq('id', ruleId)
          .select()
          .single();

        if (error) throw error;

        const updatedRule = data as CompensationRule;

        // Log rule deactivation (no specific profileId)
        await logAssignmentHistory('deactivated', updatedRule, undefined, 'Compensation rule deactivated');

        return updatedRule;
      } catch (error) {
        console.error('[useCompensationMutations] Error deactivating rule:', error);
        throw error;
      }
    },
    onSuccess: (updatedRule) => {
      toast({
        title: "Rule Deactivated",
        description: `${updatedRule.rule_name} has been deactivated.`,
        duration: 5000,
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: compensationKeys.rules() });
      queryClient.invalidateQueries({ queryKey: [...compensationKeys.all, 'company-rules'] });
    }
  });

  // Delete compensation rule mutation
  const deleteCompensationRule = useMutation({
    mutationFn: async (ruleId: string) => {
      try {
        // Check if rule is currently being validated
        // (Remove profile-specific validation since rules are templates)

        // Get current rule to check if it affects assignments
        const { data: currentRule } = await supabase
          .from('compensation_rules')
          .select('*')
          .eq('id', ruleId)
          .single();

        if (!currentRule) {
          throw new Error('Rule not found');
        }

        // Check for active assignments before deletion
        const { data: assignments } = await supabase
          .from('compensation_rule_assignments')
          .select('id')
          .eq('rule_id', ruleId)
          .eq('is_active', true);

        if (assignments && assignments.length > 0) {
          throw new Error(`Cannot delete rule with ${assignments.length} active assignment(s). Unassign the rule first.`);
        }

        const { error } = await supabase
          .from('compensation_rules')
          .delete()
          .eq('id', ruleId);

        if (error) throw error;

        // Log rule deletion (no specific profileId)
        await logAssignmentHistory('deactivated', currentRule, undefined, 'Compensation rule deleted');

        return ruleId;
      } catch (error) {
        console.error('[useCompensationMutations] Error deleting rule:', error);
        throw error;
      }
    },
    onSuccess: () => {
      toast({
        title: "Rule Deleted",
        description: "The compensation rule has been deleted successfully.",
        duration: 5000,
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: compensationKeys.rules() });
      queryClient.invalidateQueries({ queryKey: [...compensationKeys.all, 'company-rules'] });
    }
  });

  // Reactivate compensation rule mutation
  const reactivateCompensationRule = useMutation({
    mutationFn: async (ruleId: string) => {
      try {
        const { data, error } = await supabase
          .from('compensation_rules')
          .update({ is_active: true })
          .eq('id', ruleId)
          .select()
          .single();

        if (error) throw error;

        const reactivatedRule = data as CompensationRule;

        // Log rule reactivation (no specific profileId)
        await logAssignmentHistory('updated', reactivatedRule, undefined, 'Compensation rule reactivated');

        return reactivatedRule;
      } catch (error) {
        console.error('[useCompensationMutations] Error reactivating rule:', error);
        throw error;
      }
    },
    onSuccess: (reactivatedRule) => {
      toast({
        title: "Rule Reactivated",
        description: `${reactivatedRule.rule_name} has been reactivated.`,
        duration: 5000,
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: compensationKeys.rules() });
      queryClient.invalidateQueries({ queryKey: [...compensationKeys.all, 'company-rules'] });
    }
  });

  // Generate payroll with compensation rules
  const generatePayrollWithCompensation = useMutation({
    mutationFn: async (payrollData: { 
      startDate: string; 
      endDate: string; 
      profileIds: string[]; 
    }) => {
      try {
        // Direct payroll generation without RPC for now
        const { compensationService } = await import('@/services/compensation/compensationService');
        return await compensationService.generatePayrollPreview({
          profileIds: payrollData.profileIds,
          startDate: new Date(payrollData.startDate),
          endDate: new Date(payrollData.endDate)
        });
      } catch (error) {
        console.error('[useCompensationMutations] Error generating payroll:', error);
        throw error;
      }
    },
    onSuccess: () => {
      toast({
        title: "Payroll Generated",
        description: "Payroll has been generated successfully with compensation rules applied.",
        duration: 5000,
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['payroll-data'] });
    }
  });

  // Bulk update compensation rules
  const bulkUpdateCompensationRules = useMutation({
    mutationFn: async (updates: { id: string; updates: Partial<CompensationRule> }[]) => {
      try {
        const results = await Promise.all(
          updates.map(async ({ id, updates: ruleUpdates }) => {
            const { data, error } = await supabase
              .from('compensation_rules')
              .update(ruleUpdates)
              .eq('id', id)
              .select()
              .single();

            if (error) throw error;
            return data as CompensationRule;
          })
        );

        // Log bulk update (no specific profileId since rules are generic)
        results.forEach(async (rule) => {
          await logAssignmentHistory('updated', rule, undefined, `Bulk updated ${results.length} rules`);
        });

        return results;
      } catch (error) {
        console.error('[useCompensationMutations] Error bulk updating rules:', error);
        throw error;
      }
    },
    onSuccess: (rules) => {
      toast({
        title: "Rules Updated",
        description: `${rules.length} compensation rules have been updated successfully.`,
        duration: 5000,
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: compensationKeys.rules() });
      queryClient.invalidateQueries({ queryKey: [...compensationKeys.all, 'company-rules'] });
    }
  });

  // Assignment-related mutations using the new assignment system
  const assignRuleToEmployees = useMutation({
    mutationFn: async (variables: { ruleId: string; profileIds: string[] }) => {
      try {
        // Use the service method instead of RPC
        const { compensationService } = await import('@/services/compensation/compensationService');
        return await compensationService.assignRuleToEmployees(
          variables.ruleId, 
          variables.profileIds
        );
      } catch (error) {
        console.error('[useCompensationMutations] Error assigning rule:', error);
        throw error;
      }
    },
    onSuccess: (data, variables) => {
      // Log assignment history for each assignment
      if (Array.isArray(data)) {
        data.forEach(async (assignment: any) => {
          await logAssignmentHistory('assigned', {
            id: variables.ruleId,
            company_id: assignment.company_id
          }, assignment.profile_id, `Rule assigned to ${variables.profileIds.length} employee(s)`);
        });
      }

      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: compensationKeys.rules() });
      queryClient.invalidateQueries({ queryKey: [...compensationKeys.all, 'assignments'] });

      toast({
        title: "Rule Assigned",
        description: `Rule has been assigned to ${variables.profileIds.length} employee(s).`,
        duration: 5000,
      });
    }
  });

  // Unassign rule from employee
  const unassignRule = useMutation({
    mutationFn: async (variables: { ruleId: string; profileId: string }) => {
      try {
        const { error } = await supabase
          .from('compensation_rule_assignments')
          .update({ is_active: false })
          .eq('rule_id', variables.ruleId)
          .eq('profile_id', variables.profileId);

        if (error) throw error;
        return true;
      } catch (error) {
        console.error('[useCompensationMutations] Error unassigning rule:', error);
        throw error;
      }
    },
    onSuccess: (_, variables) => {
      // Log unassignment history
      logAssignmentHistory('unassigned', {
        id: variables.ruleId
      }, variables.profileId, 'Rule unassigned');

      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: compensationKeys.rules() });
      queryClient.invalidateQueries({ queryKey: [...compensationKeys.all, 'assignments'] });

      toast({
        title: "Rule Unassigned",
        description: "Rule has been unassigned from employee.",
        duration: 5000,
      });
    }
  });

  // Manual invalidation function to be called after dialog closes
  // This prevents race conditions with React portals during unmounting
  const invalidateCompensationQueries = () => {
    queryClient.invalidateQueries({ queryKey: compensationKeys.rules() });
    queryClient.invalidateQueries({ queryKey: [...compensationKeys.all, 'company-rules'] });
  };

  return {
    // Rule CRUD operations
    createCompensationRule,
    updateCompensationRule,
    deactivateCompensationRule,
    deleteCompensationRule,
    reactivateCompensationRule,
    
    // Bulk operations
    generatePayrollWithCompensation,
    bulkUpdateCompensationRules,
    
    // Assignment operations
    assignRuleToEmployees,
    unassignRule,

    // Manual invalidation
    invalidateCompensationQueries,

    // Loading states
    isCreating: createCompensationRule.isPending,
    isUpdating: updateCompensationRule.isPending,
    isDeactivating: deactivateCompensationRule.isPending,
    isDeleting: deleteCompensationRule.isPending,
    isReactivating: reactivateCompensationRule.isPending,
    isGeneratingPayroll: generatePayrollWithCompensation.isPending,
    isBulkUpdating: bulkUpdateCompensationRules.isPending,
    isAssigning: assignRuleToEmployees.isPending,
    isUnassigning: unassignRule.isPending,

    // Error states
    createError: createCompensationRule.error,
    updateError: updateCompensationRule.error,
    deactivateError: deactivateCompensationRule.error,
    deleteError: deleteCompensationRule.error,
    reactivateError: reactivateCompensationRule.error,
    generatePayrollError: generatePayrollWithCompensation.error,
    bulkUpdateError: bulkUpdateCompensationRules.error,
    assignError: assignRuleToEmployees.error,
    unassignError: unassignRule.error,
  };
};